/**
 * Activity Panel
 *
 * Right column showing activity timeline, comment form, and work note form.
 */

import { useState, useCallback } from 'react';
import type { Ticket } from '../../../types/ticket';
import type { TicketActivity } from '../../../types/ticketActivity';
import { ActivityType, ActivityVisibility, AuthorType } from '../../../types/ticketActivity';
import './ActivityPanel.css';

type ActivityFilter = 'all' | 'comments' | 'work_notes' | 'system' | 'questions';
type InputTab = 'comment' | 'worknote' | 'question';

interface ActivityPanelProps {
  ticket: Ticket;
  activities: TicketActivity[];
  currentUserId: string;
  currentUserName: string;
  onAddComment: (content: string) => Promise<void>;
  onAddWorkNote: (content: string) => Promise<void>;
  onAskQuestion?: (questionText: string) => Promise<void>;
}

export function ActivityPanel({
  ticket,
  activities,
  currentUserId,
  currentUserName,
  onAddComment,
  onAddWorkNote,
  onAskQuestion,
}: ActivityPanelProps) {
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [commentText, setCommentText] = useState('');
  const [workNoteText, setWorkNoteText] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSubmittingWorkNote, setIsSubmittingWorkNote] = useState(false);
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [activeTab, setActiveTab] = useState<InputTab>('comment');

  // Filter and sort activities (newest first)
  const filteredActivities = activities
    .filter((activity) => {
      switch (filter) {
        case 'comments':
          return activity.type === ActivityType.COMMENT;
        case 'work_notes':
          return activity.type === ActivityType.WORK_NOTE;
        case 'questions':
          return activity.type === ActivityType.QUESTION_TO_USER || activity.type === ActivityType.ANSWER_FROM_USER;
        case 'system':
          return (
            activity.type !== ActivityType.COMMENT &&
            activity.type !== ActivityType.WORK_NOTE &&
            activity.type !== ActivityType.QUESTION_TO_USER &&
            activity.type !== ActivityType.ANSWER_FROM_USER
          );
        default:
          return true;
      }
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Handle comment submission
  const handleCommentSubmit = useCallback(async () => {
    if (!commentText.trim()) return;
    setIsSubmittingComment(true);
    try {
      await onAddComment(commentText);
      setCommentText('');
    } finally {
      setIsSubmittingComment(false);
    }
  }, [commentText, onAddComment]);

  // Handle work note submission
  const handleWorkNoteSubmit = useCallback(async () => {
    if (!workNoteText.trim()) return;
    setIsSubmittingWorkNote(true);
    try {
      await onAddWorkNote(workNoteText);
      setWorkNoteText('');
    } finally {
      setIsSubmittingWorkNote(false);
    }
  }, [workNoteText, onAddWorkNote]);

  // Handle question submission
  const handleQuestionSubmit = useCallback(async () => {
    if (!questionText.trim() || !onAskQuestion) return;
    setIsSubmittingQuestion(true);
    try {
      await onAskQuestion(questionText);
      setQuestionText('');
    } finally {
      setIsSubmittingQuestion(false);
    }
  }, [questionText, onAskQuestion]);

  // Format date for display
  const formatDate = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Get activity icon
  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case ActivityType.COMMENT:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5l-3 3V3a1 1 0 0 1 1-1z" />
          </svg>
        );
      case ActivityType.WORK_NOTE:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M3 2h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm1 3v2h8V5H4zm0 4v2h6V9H4z" />
          </svg>
        );
      case ActivityType.STATUS_CHANGE:
      case ActivityType.RESOLVED:
      case ActivityType.REOPENED:
      case ActivityType.CLOSED:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2zm0 10.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
          </svg>
        );
      case ActivityType.ASSIGNED:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 2c-3.315 0-6 1.79-6 4v1h12v-1c0-2.21-2.685-4-6-4z" />
          </svg>
        );
      case ActivityType.ESCALATED:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2l5 5h-3v7H6V7H3l5-5z" />
          </svg>
        );
      case ActivityType.QUESTION_TO_USER:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 12a1 1 0 110-2 1 1 0 010 2zm1-3.5a1 1 0 01-2 0V5a1 1 0 012 0v3.5z" />
          </svg>
        );
      case ActivityType.ANSWER_FROM_USER:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 2h12a1 1 0 011 1v8a1 1 0 01-1 1H5l-3 3V3a1 1 0 011-1z" />
          </svg>
        );
      default:
        return (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="8" r="6" />
          </svg>
        );
    }
  };

  // Get activity class
  const getActivityClass = (activity: TicketActivity) => {
    if (activity.type === ActivityType.WORK_NOTE) {
      return 'activity-panel__item--work-note';
    }
    if (activity.type === ActivityType.QUESTION_TO_USER) {
      return 'activity-panel__item--question';
    }
    if (activity.type === ActivityType.ANSWER_FROM_USER) {
      return 'activity-panel__item--answer';
    }
    if (activity.type === ActivityType.COMMENT) {
      return activity.authorType === AuthorType.USER
        ? 'activity-panel__item--user-comment'
        : 'activity-panel__item--it-comment';
    }
    return 'activity-panel__item--system';
  };

  // Get display message
  const getDisplayMessage = (activity: TicketActivity) => {
    if (activity.displayMessage) return activity.displayMessage;

    switch (activity.type) {
      case ActivityType.CREATED:
        return 'Ticket created';
      case ActivityType.STATUS_CHANGE:
        return `Status changed to ${activity.newStatus}`;
      case ActivityType.ASSIGNED:
        return activity.newAssignedTechName
          ? `Assigned to ${activity.newAssignedTechName}`
          : 'Ticket assigned';
      case ActivityType.ESCALATED:
        return `Escalated to ${activity.newTier} - ${activity.newGroup}`;
      case ActivityType.PRIORITY_CHANGE:
        return `Priority changed from ${activity.previousPriority} to ${activity.newPriority}`;
      case ActivityType.RESOLVED:
        return 'Ticket resolved';
      case ActivityType.REOPENED:
        return 'Ticket reopened';
      case ActivityType.CLOSED:
        return 'Ticket closed';
      case ActivityType.QUESTION_TO_USER:
        return activity.questionText ?? 'Question sent to user';
      case ActivityType.ANSWER_FROM_USER:
        return 'User responded to question';
      default:
        return 'Activity recorded';
    }
  };

  return (
    <div className="activity-panel">
      {/* Filter tabs */}
      <div className="activity-panel__filters">
        <button
          type="button"
          className={`activity-panel__filter ${filter === 'all' ? 'activity-panel__filter--active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          type="button"
          className={`activity-panel__filter ${filter === 'comments' ? 'activity-panel__filter--active' : ''}`}
          onClick={() => setFilter('comments')}
        >
          Comments
        </button>
        <button
          type="button"
          className={`activity-panel__filter ${filter === 'work_notes' ? 'activity-panel__filter--active' : ''}`}
          onClick={() => setFilter('work_notes')}
        >
          Work Notes
        </button>
        <button
          type="button"
          className={`activity-panel__filter ${filter === 'system' ? 'activity-panel__filter--active' : ''}`}
          onClick={() => setFilter('system')}
        >
          System
        </button>
        <button
          type="button"
          className={`activity-panel__filter ${filter === 'questions' ? 'activity-panel__filter--active' : ''}`}
          onClick={() => setFilter('questions')}
        >
          Questions
        </button>
      </div>

      {/* Timeline */}
      <div className="activity-panel__timeline">
        {filteredActivities.length === 0 ? (
          <div className="activity-panel__empty">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M16 8v8l4 4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="16" cy="16" r="12" />
            </svg>
            <p>No activities to display</p>
          </div>
        ) : (
          filteredActivities.map((activity) => (
            <div key={activity.id} className={`activity-panel__item ${getActivityClass(activity)}`}>
              <div className="activity-panel__item-icon">
                {getActivityIcon(activity.type)}
              </div>
              <div className="activity-panel__item-content">
                <div className="activity-panel__item-header">
                  <span className="activity-panel__item-author">{activity.authorName}</span>
                  <span className="activity-panel__item-time">{formatDate(activity.createdAt)}</span>
                </div>
                {activity.content ? (
                  <p className="activity-panel__item-text">{activity.content}</p>
                ) : (
                  <p className="activity-panel__item-message">{getDisplayMessage(activity)}</p>
                )}
                {activity.type === ActivityType.WORK_NOTE && (
                  <span className="activity-panel__item-badge">Internal Note</span>
                )}
                {activity.type === ActivityType.QUESTION_TO_USER && (
                  <span className="activity-panel__item-badge activity-panel__item-badge--question">
                    {activity.isAnswered ? 'Answered' : 'Awaiting Response'}
                  </span>
                )}
                {activity.type === ActivityType.ANSWER_FROM_USER && (
                  <span className="activity-panel__item-badge activity-panel__item-badge--answer">User Response</span>
                )}
                {activity.editHistory && activity.editHistory.length > 0 && (
                  <span className="activity-panel__item-edited">Edited</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input forms */}
      <div className="activity-panel__input">
        <div className="activity-panel__input-tabs">
          <button
            type="button"
            className={`activity-panel__input-tab ${activeTab === 'comment' ? 'activity-panel__input-tab--active' : ''}`}
            onClick={() => setActiveTab('comment')}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5l-3 3V3a1 1 0 0 1 1-1z" />
            </svg>
            Comment
          </button>
          <button
            type="button"
            className={`activity-panel__input-tab activity-panel__input-tab--worknote ${activeTab === 'worknote' ? 'activity-panel__input-tab--active' : ''}`}
            onClick={() => setActiveTab('worknote')}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3 2h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm1 3v2h8V5H4zm0 4v2h6V9H4z" />
            </svg>
            Work Note
          </button>
          {onAskQuestion && (
            <button
              type="button"
              className={`activity-panel__input-tab activity-panel__input-tab--question ${activeTab === 'question' ? 'activity-panel__input-tab--active' : ''}`}
              onClick={() => setActiveTab('question')}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 12a1 1 0 110-2 1 1 0 010 2zm1-3.5a1 1 0 01-2 0V5a1 1 0 012 0v3.5z" />
              </svg>
              Ask Question
            </button>
          )}
        </div>

        {activeTab === 'comment' && (
          <div className="activity-panel__form">
            <textarea
              className="activity-panel__textarea"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a public comment (visible to requester)..."
              rows={3}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  handleCommentSubmit();
                }
              }}
            />
            <div className="activity-panel__form-footer">
              <span className="activity-panel__hint">Ctrl+Enter to submit</span>
              <button
                type="button"
                className="activity-panel__submit"
                onClick={handleCommentSubmit}
                disabled={isSubmittingComment || !commentText.trim()}
              >
                {isSubmittingComment ? 'Sending...' : 'Send Comment'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'worknote' && (
          <div className="activity-panel__form activity-panel__form--worknote">
            <div className="activity-panel__worknote-warning">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 11a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-3.5a1 1 0 0 1-2 0v-4a1 1 0 0 1 2 0v4z" />
              </svg>
              Internal only - not visible to requester
            </div>
            <textarea
              className="activity-panel__textarea activity-panel__textarea--worknote"
              value={workNoteText}
              onChange={(e) => setWorkNoteText(e.target.value)}
              placeholder="Add an internal work note (investigation notes, commands run, etc.)..."
              rows={3}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  handleWorkNoteSubmit();
                }
              }}
            />
            <div className="activity-panel__form-footer">
              <span className="activity-panel__hint">Ctrl+Enter to submit</span>
              <button
                type="button"
                className="activity-panel__submit activity-panel__submit--worknote"
                onClick={handleWorkNoteSubmit}
                disabled={isSubmittingWorkNote || !workNoteText.trim()}
              >
                {isSubmittingWorkNote ? 'Adding...' : 'Add Work Note'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'question' && onAskQuestion && (
          <div className="activity-panel__form activity-panel__form--question">
            <div className="activity-panel__question-info">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 12a1 1 0 110-2 1 1 0 010 2zm1-3.5a1 1 0 01-2 0V5a1 1 0 012 0v3.5z" />
              </svg>
              Sends question to user and sets status to Waiting - User
            </div>
            <textarea
              className="activity-panel__textarea activity-panel__textarea--question"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Ask the user a question (e.g., 'Can you describe the error message you're seeing?')..."
              rows={3}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  handleQuestionSubmit();
                }
              }}
            />
            <div className="activity-panel__form-footer">
              <span className="activity-panel__hint">Ctrl+Enter to submit</span>
              <button
                type="button"
                className="activity-panel__submit activity-panel__submit--question"
                onClick={handleQuestionSubmit}
                disabled={isSubmittingQuestion || !questionText.trim()}
              >
                {isSubmittingQuestion ? 'Sending...' : 'Ask Question'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
